import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import apiClient, { fetchUALEvents } from "../api/client";
import { UALEvent } from "../types";
import DataTable from "../components/DataTable";
import DateRangePicker from "../components/DateRangePicker";
import ExportButton from "../components/ExportButton";
import EventDetailModal from "../components/EventDetailModal";
import ShowSystemToggle from "../components/ShowSystemToggle";
import Button from "../components/Button";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime, utcDaysAgo, utcToday } from "../utils/datetime";

function today(): string {
  return utcToday();
}
function sevenDaysAgo(): string {
  return utcDaysAgo(7);
}

export default function AuditLogPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [events, setEvents] = useState<UALEvent[]>([]);
  const [operations, setOperations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(sevenDaysAgo());
  const [dateTo, setDateTo] = useState(today());
  const [userFilter, setUserFilter] = useState("");
  const [operationFilter, setOperationFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showSystem, setShowSystem] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<UALEvent | null>(null);

  // Load operations list for dropdown
  useEffect(() => {
    apiClient
      .get<string[]>("/api/ual-events/operations")
      .then((res) => setOperations(res.data))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = { limit: "1000" };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (userFilter) params.user_id = userFilter;
    if (operationFilter) params.operation = operationFilter;
    if (categoryFilter) params.change_category = categoryFilter;
    if (showSystem) params.show_system = "true";

    fetchUALEvents(params)
      .then((data) => setEvents(data))
      .catch((err) => setError(err.message ?? "Failed to load audit events."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, userFilter, operationFilter, categoryFilter, showSystem]);

  // Text and date filters wait for Apply, but the system-activity switch is a
  // view mode rather than a query the user is composing, so it refetches at once.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSystem]);

  const columns: ColumnDef<UALEvent, string>[] = [
    {
      accessorKey: "creationTime",
      header: "Date / Time",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    { accessorKey: "userId", header: "User", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "operation", header: "Operation", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "objectId", header: "Object", cell: ({ getValue }) => <span className="max-w-xs truncate block" title={getValue()}>{getValue() || "—"}</span> },
    { accessorKey: "clientIp", header: "IP Address", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "resultStatus", header: "Result", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "changeCategory", header: "Category", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workload", header: "Source", cell: ({ getValue }) => getValue() || "—" },
    {
      id: "details",
      header: "",
      cell: ({ row }) => (
        <button
          onClick={(e) => { e.stopPropagation(); setSelectedEvent(row.original); }}
          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
        >
          Details
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            O365 Unified Audit Log — authoritative audit trail for SOX compliance.
          </p>
        </div>
        <ExportButton
          endpoint="/api/ual-events/export"
          params={{
            date_from: dateFrom,
            date_to: dateTo,
            user_id: userFilter || undefined,
            operation: operationFilter || undefined,
            change_category: categoryFilter || undefined,
            show_system: showSystem ? "true" : undefined,
          }}
          filename="audit-log-export.csv"
        />
      </div>

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <DateRangePicker
            label="Date range:"
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
          />
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">User / Email</label>
            <input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="user@example.com"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-52"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Operation</label>
            <select
              value={operationFilter}
              onChange={(e) => setOperationFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-52"
            >
              <option value="">All Operations</option>
              {operations.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-40"
            >
              <option value="">All</option>
              <option value="report">Report</option>
              <option value="connection">Connection</option>
              <option value="schedule">Schedule</option>
            </select>
          </div>
          <Button variant="primary" onClick={fetchData} className="self-end">
            Apply Filters
          </Button>
          <div className="self-end pb-1.5">
            <ShowSystemToggle show={showSystem} onChange={setShowSystem} />
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading audit events…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-500">
            {events.length} event{events.length !== 1 ? "s" : ""} returned
            {events.length >= 1000 && " (limit reached — narrow your date range)"}
          </p>
          <DataTable data={events} columns={columns} initialPageSize={50} />
        </>
      )}

      {/* Modal */}
      <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  );
}
